import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PartyPopper, Clock, Users, Shield, ArrowRight, Calendar } from "lucide-react";
import { CHAIR_MASSAGE } from "@/data/extras";
import { THERAPIST } from "@/data/catalog";
import useSEO from "@/hooks/useSEO";
import ReserveButton from "@/components/ReserveButton";

// Événements privés : mariages, anniversaires, journées bien-être, retraites — même tarif que le chair massage entreprise.
export default function Events() {
    useSEO({
        title: "Massage événementiel à Blainville · Jérémy Tessier",
        description: "Massage sur chaise pour événements privés à Blainville et sur la Rive-Nord — mariages, anniversaires, retraites bien-être. 90 $/heure. Reçu d'assurance fourni.",
        canonical: "https://massoclic.ca/evenements",
        ogImage: "https://massoclic.ca/icon.svg",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Massage événementiel sur chaise",
            provider: {
                "@type": "HealthAndBeautyBusiness",
                name: "Jérémy Tessier — Massothérapie",
                telephone: THERAPIST.phone,
                url: "https://massoclic.ca",
            },
            areaServed: "Rive-Nord de Montréal",
            offers: CHAIR_MASSAGE.options.map((o) => ({
                "@type": "Offer",
                price: o.price,
                priceCurrency: "CAD",
                name: `Massage événement ${o.duration} min`,
            })),
        },
    });

    const mailSubject = encodeURIComponent("Demande — Massage événement");
    const mailBody = encodeURIComponent(
        "Bonjour Jérémy,\n\nJe souhaite intégrer un service de massage à un événement. Détails :\n\n• Type d'événement (mariage, anniversaire, retraite, autre) : \n• Date : \n• Lieu : \n• Nombre de participants estimé : \n• Durée par personne (15/30/45/60 min) : \n\nMerci !"
    );

    return (
        <main data-testid="events-page" className="pt-32 md:pt-40 pb-20">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-14">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <PartyPopper size={14} className="text-[#E07A5F]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            Événements privés
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        Une touche{" "}
                        <span className="italic text-[#E07A5F]">bien-être</span> pour vos événements
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed">
                        Mariages, anniversaires, retraites d&apos;équipe, journées bien-être, baby-showers — j&apos;offre un
                        service de massage sur chaise professionnel pour rendre vos moments encore plus mémorables.
                        Ambiance calme garantie, sans besoin de logistique.
                    </p>
                    <p className="mt-4 text-sm text-[#3D8C95] italic font-medium">
                        Reçu d&apos;assurance fourni pour chaque participant.
                    </p>
                    <div className="mt-6">
                        <ReserveButton
                            mailSubject="Massage événement — Demande"
                            mailBody={"Bonjour Jérémy,\n\nJe souhaite intégrer un service de massage à un événement.\n\n• Type d'événement (mariage, anniversaire, retraite, autre) : \n• Date : \n• Lieu : \n• Nombre de participants estimé : \n• Durée par personne (15/30/45/60 min) : \n\nMerci !"}
                            quoteSubject="Devis — Massage événement"
                            quoteBody={"Bonjour Jérémy,\n\nJe souhaite un devis pour un massage lors d'un événement.\n\n• Type d'événement : \n• Date : \n• Lieu : \n• Nombre de participants : \n• Durée souhaitée : \n\nMerci !"}
                        />
                    </div>
                </div>

                {/* Tarifs (identiques au corporate) */}
                <section className="mb-14">
                    <h2 className="font-display text-2xl md:text-3xl text-[#2C2A29] mb-2">Tarifs</h2>
                    <p className="text-[#6E6862] mb-8">
                        <b>{CHAIR_MASSAGE.hourlyRate} $ de l&apos;heure</b> · fractionnable en séances plus courtes selon vos invités.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {CHAIR_MASSAGE.options.map((o) => (
                            <div
                                key={o.id}
                                data-testid={`event-option-${o.duration}`}
                                className={`rounded-3xl p-6 md:p-7 border transition-all hover:-translate-y-1 ${
                                    o.duration === 30
                                        ? "bg-[#E07A5F] text-white border-[#E07A5F] shadow-[0_20px_50px_rgba(224,122,95,0.35)]"
                                        : "bg-white border-[#E8E2D9] shadow-[0_8px_30px_rgba(44,42,41,0.04)]"
                                }`}
                            >
                                <Clock size={20} className={o.duration === 30 ? "text-white/85" : "text-[#E07A5F]"} />
                                <div className="mt-3 font-display text-4xl">
                                    {o.duration}
                                    <span className={`text-sm ${o.duration === 30 ? "text-white/80" : "text-[#6E6862]"}`}> min</span>
                                </div>
                                <div className={`mt-4 pt-4 border-t ${o.duration === 30 ? "border-white/20" : "border-[#E8E2D9]"}`}>
                                    <span className="font-display text-3xl">{o.price}</span>
                                    <span className={`text-sm ${o.duration === 30 ? "text-white/80" : "text-[#6E6862]"}`}> $ / personne</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Occasions */}
                <section className="mb-14">
                    <h2 className="font-display text-2xl md:text-3xl text-[#2C2A29] mb-6">
                        Idéal pour
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                            { icon: "💍", title: "Mariages", desc: "Un moment de détente pour les invités entre la cérémonie et la soirée." },
                            { icon: "🎂", title: "Anniversaires", desc: "Un cadeau inoubliable pour l'honoré(e) et ses proches." },
                            { icon: "🌿", title: "Retraites bien-être", desc: "Compléter votre programme yoga/méditation par un soin corporel." },
                            { icon: "🎉", title: "Événements RH", desc: "Team-building et journées de reconnaissance en entreprise." },
                            { icon: "👶", title: "Baby-showers", desc: "Chouchouter la future maman et ses invités." },
                            { icon: "🏆", title: "Événements sportifs", desc: "Récupération musculaire après une course ou un tournoi." },
                        ].map((occ, i) => (
                            <div
                                key={i}
                                data-testid={`event-occasion-${i}`}
                                className="rounded-2xl p-5 bg-white border border-[#E8E2D9]"
                            >
                                <div className="text-3xl mb-2">{occ.icon}</div>
                                <h3 className="font-display text-lg text-[#2C2A29]">{occ.title}</h3>
                                <p className="text-xs text-[#6E6862] mt-1 leading-relaxed">{occ.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Avantages */}
                <section className="mb-14 grid md:grid-cols-3 gap-6">
                    {[
                        { icon: Users, title: "Clef en main", desc: "Je viens avec ma chaise professionnelle. Vous n'avez rien à préparer." },
                        { icon: Calendar, title: "Flexibilité totale", desc: "Adapté à votre horaire et au nombre d'invités estimé." },
                        { icon: Shield, title: "Reçus d'assurance", desc: "Chaque participant reçoit un reçu remboursable." },
                    ].map((f, i) => (
                        <div key={i} className="rounded-3xl p-6 bg-white border border-[#E8E2D9]">
                            <div className="w-12 h-12 rounded-2xl bg-[#E07A5F]/10 flex items-center justify-center mb-4">
                                <f.icon size={22} className="text-[#E07A5F]" />
                            </div>
                            <h3 className="font-display text-xl text-[#2C2A29]">{f.title}</h3>
                            <p className="mt-2 text-sm text-[#6E6862] leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </section>

                {/* CTA */}
                <div className="rounded-3xl bg-[#2C2A29] text-white p-8 md:p-12 text-center">
                    <h2 className="font-display text-3xl md:text-4xl leading-tight">
                        Parlons de votre événement
                    </h2>
                    <p className="mt-3 text-white/75 max-w-2xl mx-auto leading-relaxed">
                        Envoyez-moi vos détails — je vous prépare une proposition personnalisée dans les 24 h.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <a
                            href={`mailto:${THERAPIST.email}?subject=${mailSubject}&body=${mailBody}`}
                            data-testid="event-mail-cta"
                        >
                            <Button className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6 text-base">
                                Demander un devis <ArrowRight size={16} className="ml-2" />
                            </Button>
                        </a>
                        <a href={`tel:${THERAPIST.phone}`}>
                            <Button variant="outline" className="rounded-full border-white text-white hover:bg-white hover:text-[#2C2A29] px-8 py-6 text-base">
                                {THERAPIST.phone}
                            </Button>
                        </a>
                    </div>
                </div>

                <div className="mt-10 text-center">
                    <Link to="/menu" className="text-sm text-[#3D8C95] hover:text-[#2C6D75]">
                        ← Voir tout le menu détaillé
                    </Link>
                </div>
            </div>
        </main>
    );
}
